import type { Game } from './api.js';
import { lotteryState } from '../../components/lobby/cardMeta.js';

/**
 * Sort lottery-heavy game lists so the player sees what's actionable first:
 *  1. Open lottery — soonest-closing first (the round about to lock is most urgent).
 *  2. Syncing lottery (status open but our cached close time is already stale) —
 *     grouped by family, most-recent first; it is about to refresh into a fresh
 *     open round, so it ranks above fully-closed rounds.
 *  3. Closed lottery — grouped by family (yi-ki, stock, country) with the most
 *     recently-closed family first, newest round first within a family.
 *  4. Non-lottery — original backend order preserved.
 *
 * Open/closed/syncing is decided by the same `lotteryState` the card badge uses,
 * so whatever renders "เปิดรับ" always sorts to the top.
 */
function lotteryFamily(name: string): string {
  if (/ยี่กี/i.test(name)) return 'yi-ki';
  if (/หวยหุ้น/i.test(name)) return 'stock';
  return name.replace(/\s*\(.+?\)\s*/g, '').trim() || name;
}

function parseRoundNumber(name: string): number {
  const m = /รอบที่\s*(\d+)/.exec(name);
  return m ? Number(m[1]) : 0;
}

const STATE_BUCKET = { open: 1, syncing: 2, closed: 3 } as const;

export function sortLotteryFirst<T extends Game>(games: T[]): T[] {
  const now = Date.now();
  type Item = { g: T; idx: number; bucket: number; family: string; target: number; round: number };
  const items: Item[] = games.map((g, idx) => {
    const isLottery = g.category === 'lottery' || /^หวย/.test(g.name);
    if (!isLottery) return { g, idx, bucket: 4, family: '', target: 0, round: 0 };
    const raw = g.ticketBefore ? new Date(g.ticketBefore).getTime() : NaN;
    const knownTarget = Number.isFinite(raw);
    const expired = !knownTarget || raw <= now;
    const bucket = STATE_BUCKET[lotteryState(g, expired)];
    return { g, idx, bucket, family: lotteryFamily(g.name), target: knownTarget ? raw : 0, round: parseRoundNumber(g.name) };
  });
  const familyAnchor = new Map<string, number>();
  for (const it of items) {
    if (it.bucket === 4 || !it.family) continue;
    const key = `${it.bucket}|${it.family}`;
    const cur = familyAnchor.get(key);
    if (cur === undefined || it.target > cur) familyAnchor.set(key, it.target);
  }
  return items
    .sort((a, b) => {
      if (a.bucket !== b.bucket) return a.bucket - b.bucket;
      if (a.bucket === 4) return a.idx - b.idx;
      const aKey = familyAnchor.get(`${a.bucket}|${a.family}`) ?? 0;
      const bKey = familyAnchor.get(`${b.bucket}|${b.family}`) ?? 0;
      if (aKey !== bKey) return a.bucket === 1 ? aKey - bKey : bKey - aKey;
      if (a.family !== b.family) return a.family.localeCompare(b.family);
      if (a.bucket === 1) return a.target - b.target || a.idx - b.idx;
      if (a.round !== b.round) return b.round - a.round;
      if (a.target !== b.target) return b.target - a.target;
      return a.idx - b.idx;
    })
    .map(({ g }) => g);
}
